import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, UserPlus, FileSpreadsheet, ScanLine } from 'lucide-react';

interface AddMembreMenuProps {
  onIndividuel: () => void;
  onCSV: () => void;
  onScan: () => void;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Menu dropdown "+ Ajouter" avec 3 modes :
 * 1. Individuel — ajouter un membre manuellement
 * 2. Import CSV — import en masse depuis un fichier
 * 3. Scan attestations — upload multiple de PDFs pour extraction IA
 * 
 * Masqué si disabled (utilisateur non admin/owner)
 */
export const AddMembreMenu = ({ onIndividuel, onCSV, onScan, disabled, loading }: AddMembreMenuProps) => {
  const [open, setOpen] = useState(false);

  // Si l'utilisateur n'a pas la permission, on préserve l'emplacement
  // sans afficher le CTA.
  if (!loading && disabled) {
    return <div className="h-10 w-[108px] shrink-0" aria-hidden="true" />;
  }

  const items = [
    {
      icon: UserPlus,
      label: 'Ajout individuel',
      description: 'Ajouter un membre manuellement',
      onClick: onIndividuel,
    },
    {
      icon: FileSpreadsheet,
      label: 'Import CSV',
      description: 'Importer une liste depuis un fichier',
      onClick: onCSV,
    },
    {
      icon: ScanLine,
      label: 'Scan d\'attestations',
      description: 'Uploader des attestations en masse',
      onClick: onScan,
    },
  ];

  return (
    <div className="relative">
      <Button
        onClick={() => setOpen(!open)}
        disabled={loading}
        aria-busy={loading}
        className="h-10 px-4 text-sm"
      >
        <Plus className="h-4 w-4" />
        Ajouter
      </Button>

      {/* Dropdown */}
      {open && (
        <>
          {/* Overlay pour fermer */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setOpen(false)} 
          />
          
          {/* Menu */}
          <div className="app-surface absolute right-0 top-full z-50 mt-2 w-[320px] overflow-hidden rounded-[20px]">
            {items.map((item, i) => (
              <button
                key={item.label}
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
                className={`w-full px-4 py-3.5 text-left transition-colors hover:bg-[hsl(var(--app-surface-muted))] ${
                  i > 0 ? 'border-t border-[hsl(var(--app-border-subtle))]' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 rounded-[14px] bg-[hsl(var(--app-accent-soft))] p-2.5 text-[hsl(var(--app-text))]">
                    <item.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[15px] font-medium text-[hsl(var(--app-text-strong))]">{item.label}</p>
                    <p className="mt-0.5 text-[13px] leading-5 text-[hsl(var(--app-text-muted))]">{item.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
